import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    FormControl,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
    ToggleButtonGroup,
    ToggleButton
} from '@mui/material';
import {
    PieChart as PieChartIcon,
    BarChart as BarChartIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { PieChart, Pie, BarChart, Bar, Cell, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import TEXT from './text';
import * as calculationService from './services/calculationService';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF6B9D'];

const RecordsDistributionChart = ({ groupId, storeName }) => {
    const language = useSelector(state => state.App_Settings.language);
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [columns, setColumns] = useState([]);
    const [selectedColumn, setSelectedColumn] = useState('');
    const [chartType, setChartType] = useState('pie'); // 'pie' or 'bar'
    const [chartData, setChartData] = useState([]);

    // Load available columns
    useEffect(() => {
        loadColumns();
    }, [groupId]);

    // Load chart data when column changes
    useEffect(() => {
        if (selectedColumn) {
            loadChartData();
        }
    }, [selectedColumn, groupId]);

    const loadColumns = async () => {
        try {
            const availableColumns = await calculationService.getAvailableColumns(groupId, { storeName });
            setColumns(availableColumns);
            
            // Select first column by default
            if (availableColumns.length > 0 && !selectedColumn) {
                setSelectedColumn(availableColumns[0]);
            }
        } catch (err) {
            console.error('[Distribution] Error loading columns:', err);
            setError(err.message);
        }
    };

    const loadChartData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const breakdown = await calculationService.getDashboardData(
                groupId,
                'column_breakdown',
                { 
                    storeName,
                    column_name: selectedColumn
                }
            );
            
            // Transform data for Recharts
            const transformedData = breakdown.map((item, index) => ({
                name: item.value,
                value: item.count,
                percentage: item.percentage,
                fill: COLORS[index % COLORS.length]
            }));
            
            setChartData(transformedData);
        } catch (err) {
            console.error('[Distribution] Error loading chart data:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleColumnChange = (event) => {
        setSelectedColumn(event.target.value);
    };

    const handleChartTypeChange = (event, newType) => {
        if (newType !== null) {
            setChartType(newType);
        }
    };

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            return (
                <Box sx={{ bgcolor: 'background.paper', p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="body2">{payload[0].name}</Typography>
                    <Typography variant="body2" color="primary">
                        {TEXT.Count[language]}: {payload[0].value}
                    </Typography>
                    <Typography variant="body2" color="secondary">
                        {TEXT.Percentage[language]}: {payload[0].payload.percentage.toFixed(1)}%
                    </Typography>
                </Box>
            );
        }
        return null;
    };

    return (
        <Box>
            {/* Header with controls */}
            <Box sx={{ mb: { xs: 1, sm: 2 }, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: { xs: 1, sm: 2 } }}>
                <Typography variant={{ xs: 'subtitle1', sm: 'h6' }}>
                    {TEXT.RecordsDistribution[language]}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {/* Column selector */}
                    <FormControl size="small" >
                        <Select
                            value={selectedColumn}
                            onChange={handleColumnChange}
                            displayEmpty
                        >
                            {columns.length === 0 ? (
                                <MenuItem value="">
                                    {TEXT.NoColumnsAvailable[language]}
                                </MenuItem>
                            ) : (
                                columns.map((col) => (
                                    <MenuItem key={col} value={col}>
                                        {col}
                                    </MenuItem>
                                ))
                            )}
                        </Select>
                    </FormControl>
                    
                    {/* Chart type toggle */}
                    <ToggleButtonGroup
                        value={chartType}
                        exclusive
                        onChange={handleChartTypeChange}
                        size="small"
                    >
                        <ToggleButton value="pie">
                            <PieChartIcon fontSize="small" />
                        </ToggleButton>
                        <ToggleButton value="bar">
                            <BarChartIcon fontSize="small" />
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            </Box>

            {/* Chart area */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: { xs: 180, sm: 220, md: 300 } }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Alert severity="error">{error}</Alert>
            ) : chartData.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: { xs: 180, sm: 220, md: 300 } }}>
                    <Typography color="text.secondary">
                        {TEXT.NoData[language]}
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ height: { xs: 180, sm: 220, md: 300 } }}>
                    <ResponsiveContainer width="100%" height="100%">
                        {chartType === 'pie' ? (
                            <PieChart>
                                <Pie
                                    data={chartData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, percentage }) => `${name}: ${percentage.toFixed(1)}%`}
                                    outerRadius={70}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {chartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.fill} />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                            </PieChart>
                        ) : (
                            <BarChart data={chartData}>
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="value" fill="#8884d8">
                                    {chartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.fill} />
                                    ))}
                                </Bar>
                            </BarChart>
                        )}
                    </ResponsiveContainer>
                </Box>
            )}
        </Box>
    );
};

export default RecordsDistributionChart;
